import type { ReactNode } from "react";

import { BTText } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { extractSubtitle, stripMarkdownBold } from "./AiSearchResultCard.utils";
import { getEntityMeta } from "./entityMeta";
import { highlightMatches } from "./highlight";
import type { ICategorizedHit } from "./types";

interface IAiSearchResultCardProps {
    hit: ICategorizedHit;
    query: string;
    isHighlighted: boolean;
    isPending: boolean;
    elementId: string;
    onClick: () => void;
}

function extractTitle(hit: ICategorizedHit): string {
    if (hit.summary) {
        const firstLine = hit.summary.split("\n")[0].trim();
        if (firstLine.length > 0) {
            const cleaned = stripMarkdownBold(firstLine);
            if (cleaned.trim().length > 0) return cleaned;
        }
    }
    const meta = getEntityMeta(hit.entity);
    return `${meta.label} #${hit.id}`;
}

export function AiSearchResultCard(props: IAiSearchResultCardProps) {
    const { hit, query, isHighlighted, isPending, elementId, onClick } = props;
    const meta = getEntityMeta(hit.entity);
    const title = extractTitle(hit);
    const subtitle = extractSubtitle(hit);

    const titleContent: ReactNode = query ? highlightMatches(title, query) : title;

    return (
        <div
            className={cn("ai-search-result-card", {
                "ai-search-result-card--highlighted": isHighlighted,
                "ai-search-result-card--pending": isPending,
            })}
            id={elementId}
            role="option"
            tabIndex={0}
            aria-selected={isHighlighted}
            aria-busy={isPending}
            onClick={isPending ? undefined : onClick}
            data-testid="ai-search-result-card"
        >
            <div className="ai-search-result-card-row">
                <div className="ai-search-result-icon" aria-hidden="true">
                    {meta.icon}
                </div>
                <div className="ai-search-result-content">
                    <div className="ai-search-result-top">
                        <span className="ai-search-result-entity">{meta.label}</span>
                    </div>
                    <BTText
                        size="distinct-md"
                        tag="span"
                        className="ai-search-result-title"
                        testid="ai-search-result-title"
                    >
                        {titleContent}
                    </BTText>
                    {subtitle && <div className="ai-search-result-subtitle">{subtitle}</div>}
                </div>
            </div>
        </div>
    );
}
